import React, { useEffect, useState } from 'react';
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	FormControlLabel,
	Radio,
	RadioGroup,
	TextField,
	Checkbox,
	IconButton,
	Button,
	Box,
	Typography,
	Link,
	List,
	ListItem,
	ListItemText,
	Divider,
} from '@mui/material';
import PrimaryButton from '../button/PrimaryButton';
import Text from '../text';
import ApiService from 'utils/services/ApiService';
import LoadingPopup from './LoadingDeleteAccount';
import { useMsal } from '@azure/msal-react';
import CloseIcon from '@mui/icons-material/Close';
import CONSTANTS from '../../utils/constants/UIConstants';

interface DataControlSettingsModalProps {
	open: boolean;
	onClose: () => void;
	onConfirm?: (reason: string, customReason?: string) => void;
}

const DataControlSettingsModal: React.FC<DataControlSettingsModalProps> = ({
	open,
	onClose,
	onConfirm,
}) => {
	const [loading, setLoading] = useState(false);
	// Initialize state from localStorage, default to 'cloud' if not set
	const [storageMode, setStorageMode] = useState<'cloud' | 'local'>(
		localStorage.getItem('localStorage') === 'true' ? 'local' : 'cloud'
	);

	return (
		<Dialog
			open={open}
			onClose={(event, reason) => {
				if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
					onClose();
				}
			}}
			fullWidth
			sx={{
				'& .MuiDialog-paper': {
					borderRadius: '8px',
					padding: '24px',
					margin: '16px',
					maxWidth: '800px',
					fontFamily: 'Jost !important'
				},
				'& .MuiDialogContent-root': {
					padding: '0px !important',
				},
			}}
		>
			{loading && <LoadingPopup open={loading} />}
			<DialogContent
				sx={{
					overflowY: 'unset',
				}}
			>
				{/* Header Section */}
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						mb: 2,
					}}
				>
					<Text addStyle="!font-inter !font-bold !text-[#2C2C2C] !text-[24px] !mb-4">
						Data Control
					</Text>
					<IconButton
						onClick={onClose}
						size="small"
						sx={{ p: 0, mr: '5px', mt: '2px' }}
                    	className="absolute top-[-8px] right-0 close-main-menu"
					>
						<CloseIcon />
					</IconButton>
				</Box>

				<Typography
					className="!text-[#2C2C2C] !font-jost !text-[16px]"
					gutterBottom
				>
					Manage how your data is stored.
				</Typography>
				<Divider
					variant="middle"
					style={{
						margin: '20px 0',
						backgroundColor: CONSTANTS.APP_BACKGROUND_COLOR,
					}}
				/>
				<Typography className="!font-inter !text-[14px]" gutterBottom>
					You can choose to store test results in{' '}
					<strong>LuminUltra's secure cloud</strong> or{' '}
					<strong>locally on your PC</strong>. By default, new
					accounts use cloud storage, which enhances data security and
					supports team collaboration.
				</Typography>

				<Typography
					className="!font-inter !font-semibold !text-[18px] !mt-[15px]"
					gutterBottom
				>
					Active storage mode:
				</Typography>
				<RadioGroup value={storageMode}>
					{storageMode === 'cloud' ? (
						<>
							<Box
								sx={{
									border: `1px solid ${
										storageMode === 'cloud'
											? '#0093c8'
											: '#7dc242'
									}`,
									borderRadius: '4px',
									p: 2,
									
									backgroundColor:
										storageMode === 'cloud'
											? '#f4fcff'
											: '#f3ffe8',
									fontFamily: 'inter !important',
								}}
							>
								<Box>
									<Text
										variant="subtitle2"
										sx={{
											fontWeight: 500,
											fontSize: '16px',
											fontFamily: 'inter !important',
											color:'rgb(44,44,44)'
										}}
									>
										Cloud Storage (recommended)
									</Text>
									<DialogContentText
										sx={{
											mt: 1,
											fontFamily: 'Jost !important',
											color:'rgb(44,44,44)',
											fontSize:'14px'
										}}
									>
										Data is stored in your LuminUltra Cloud
										account to enable benefits including
										security, cross-device sync, automatic
										backups, and sharing, governed by the
										<Link
											href="https://webdataservices.luminultracloud.com/EULA.pdf"
											sx={{
												ml: 0.5,
												color: '#0088CC',
												fontFamily: 'Jost !important',
											}}
											target="_blank"
										>
											LuminUltra Cloud End User License
											Agreement
										</Link>
										.
									</DialogContentText>
								</Box>
							</Box>
						</>
					) : (
						<>
							<Box
								sx={{
									border: `1px solid ${
										storageMode === 'local'
											? '#4CAF50'
											: '#0088CC'
									}`,
									borderRadius: '4px',
									p: 2,
									backgroundColor:
										storageMode === 'local'
											? '#F9FFF9'
											: '#F8FCFF',
									fontFamily: 'Inter !important',
								}}
							>
								<Box>
									<Text
										variant="subtitle2"
										sx={{
											fontWeight: 500,
											fontSize: '16px',
											fontFamily: 'Inter !important',
											color:'rgb(44,44,44)'
										}}
									>
										Local Storage
									</Text>
									<DialogContentText
										sx={{
											mt: 1,
											fontFamily: 'Inter !important',
											fontSize:'14px',
											color:'rgb(44,44,44)'
										}}
									>
										Results are saved to your browser's
										internal database system and not 
										saved to LuminUltra servers. LuminUltra
										bears no responsibility for lost,
										damaged, or inaccessible locally-stored
										data.
									</DialogContentText>
								</Box>
							</Box>
						</>
					)}
				</RadioGroup>
				<Divider
					variant="middle"
					style={{
						margin: '30px 0',
						backgroundColor: CONSTANTS.APP_BACKGROUND_COLOR,
					}}
				/>
				<Text addStyle="!text-[#2C2C2C] !font-inter !text-[16px] !mt-[24px] !font-semibold !mb-[10px]">
					About Local Storage Mode
				</Text>
				<DialogContentText
					component="div"
					sx={{ mb: 1, fontFamily: 'Jost !important', color:'rgb(44,44,44)' }}
				>
					<List
						component="ol"
						sx={{ p: 0, m: 0, fontFamily: 'Jost !important' }}
					>
						<ListItem sx={{ display: 'list-item', pl: 0 }}>
							<Typography className="!font-inter !text-[14px]">
								<strong>1.</strong> Local Storage Mode{' '}
								<strong>
									prevents test result data from being sent to
									the cloud
								</strong>{' '}
								by storing solely results in your browser's
								database. While using local storage mode,{' '}
								<strong>
									account information, usage data and meta
									data are still stored in the cloud
								</strong>
								, as outlined in the{' '}
								<Link
									href="https://webdataservices.luminultracloud.com/EULA.pdf"
									sx={{
										color: '#0092C8',
										fontFamily: 'Inter !important',
									}}
									target="_blank"
								>
									LuminUltra Cloud End User License Agreement
								</Link>
								.
							</Typography>
						</ListItem>

						<ListItem sx={{ display: 'list-item', pl: 0 }}>
							<Typography className="!font-inter !text-[14px]">
								<strong>2.</strong> Test results stored locally
								are saved in your browser's internal database
								and remain accessible during normal use. This
								function is distinct from Offline Mode, and some
								features require an internet connection
								regardless of the data storage method used.
							</Typography>
						</ListItem>

						<ListItem sx={{ display: 'list-item', pl: 0 }}>
							<Typography className="!font-inter !text-[14px]">
								<strong>3.</strong> Collaboration features, such
								as sharing results or assigning user
								permissions, are not available in Local
								Storage Mode.
							</Typography>
						</ListItem>

						<ListItem sx={{ display: 'list-item', pl: 0 }}>
							<Typography className="!font-inter !text-[14px]">
								<strong>4.</strong> LuminUltra is not
								responsible for lost or inaccessible data stored
								locally and cannot recover it if it is not saved
								in our cloud.
							</Typography>
						</ListItem>

						<ListItem sx={{ display: 'list-item', pl: 0 }}>
							<Typography className="!font-inter !text-[14px]">
								<strong>5.</strong> Switching between storage
								modes is not recommended. If you switch from
								Local Storage to cloud storage, your local
								results will sync to the cloud. If you switch
								from cloud to Local Storage, existing cloud data
								will remain unless you request deletion via
								customer support.
							</Typography>
						</ListItem>

						<ListItem sx={{ display: 'list-item', pl: 0 }}>
							<Typography className="!font-inter !text-[14px]">
								<strong>6.</strong> Switching between more than
								one user account on the same browser and Windows
								profile is <strong>not recommended</strong>{' '}
								while in Local Storage mode. Test results are
								saved to your browser's internal database, and
								switching accounts will overwrite this database,
								resulting in data loss.
							</Typography>
						</ListItem>
					</List>
				</DialogContentText>

				<DialogContentText
					sx={{ mt: '10px', fontFamily: 'Jost !important', color:'rgb(44,44,44)' }}
				>
					<strong>
						To request a change to your active storage mode, please{' '}
					</strong>
					<Link
						href="https://my.luminultra.com/hc/en-us/requests/new"
						sx={{ color: '#0092C8', fontFamily: 'Jost !important' }}
						target="_blank"
					>
						<strong> contact support</strong>
					</Link>
					.
				</DialogContentText>
			</DialogContent>
		</Dialog>
	);
};

export default DataControlSettingsModal;
